/**
 * PRELAUNCH mode (btwkwartier.nl before the beta): only the public website, the beta waitlist and the legal pages.
 * On when GET /api/features says `prelaunch: true`, or when the web build was made with VITE_PRELAUNCH=1 (safety net:
 * the app pages never flash while /api/features loads or if it fails). In prelaunch every other /api route answers 404,
 * so the app must not probe the session either (see SessionProvider).
 */
import type { FeaturesDTO } from '@btw/shared';
import { useFeatures } from '../api/hooks';

export const PRELAUNCH_BUILD = import.meta.env.VITE_PRELAUNCH === '1';

export type LegalInfo = FeaturesDTO['legal'];

export interface PrelaunchState {
  prelaunch: boolean;
  /** True only while /api/features is being fetched the first time (and the build flag does not decide already). */
  loading: boolean;
  legal: LegalInfo;
}

export function prelaunchFrom(features: Pick<FeaturesDTO, 'prelaunch'> | undefined, buildFlag = PRELAUNCH_BUILD): boolean {
  return buildFlag || features?.prelaunch === true;
}

export function usePrelaunch(): PrelaunchState {
  const f = useFeatures();
  return {
    prelaunch: prelaunchFrom(f.data),
    loading: !PRELAUNCH_BUILD && f.isLoading,
    legal: f.data?.legal ?? {},
  };
}

/** The only pages the SPA shows in prelaunch; everything else (login, register, app pages) redirects to `/`. */
export const PRELAUNCH_PATHS = ['/', '/privacy', '/voorwaarden', '/subverwerkers', '/verwerkersovereenkomst'] as const;
export function isPrelaunchPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '') || '/';
  return (PRELAUNCH_PATHS as readonly string[]).includes(p);
}
